import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openSuscriptionModal } from "../store/actions";
import { ACTION_GUARDS, AccessLevel } from "../config/actionGuards.ts";

const selectIsUserPremium = (state: { general: { isUserPremium: boolean } }) =>
  state.general?.isUserPremium ?? false;

const selectIsGuestAccount = (state: { general: { userData?: { username?: string } } }) =>
  state.general?.userData?.username === "invited@edify.la";

const hasAccess = (
  level: AccessLevel,
  isUserPremium: boolean,
  isGuestAccount: boolean
): boolean => {
  if (level === "any") return true;
  if (level === "not_guest") return !isGuestAccount;
  return isUserPremium;
};

export function useActionGuard() {
  const dispatch = useDispatch();
  const isUserPremium = useSelector(selectIsUserPremium);
  const isGuestAccount = useSelector(selectIsGuestAccount);

  const checkAccess = useCallback(
    (actionKey: string): boolean => {
      const level = ACTION_GUARDS[actionKey] ?? "premium";
      if (hasAccess(level, isUserPremium, isGuestAccount)) return true;
      dispatch(openSuscriptionModal());
      return false;
    },
    [dispatch, isUserPremium, isGuestAccount]
  );

  const guard = useCallback(
    <T extends (...args: unknown[]) => void>(
      actionKey: string,
      handler: T,
      onAttempt?: () => void
    ) =>
      (...args: Parameters<T>) => {
        onAttempt?.();
        if (checkAccess(actionKey)) handler(...args);
      },
    [checkAccess]
  );

  return { guard, checkAccess };
}
